/**
 * Generic Filters Context Factory
 *
 * Creates a complete filter context system with:
 * - Type-safe filter state management
 * - Automatic debouncing
 * - Query string generation
 * - Auto-generated setters for each filter
 * - Optional route-based reset
 *
 * @example
 * ```tsx
 * const { Provider, useFilters } = createFiltersContext({
 *   name: "Employee",
 *   initialState: { search: "", sites: [], status: "active" as const },
 *   debounceMs: 750,
 *   buildQueryString: (state) => {
 *     const params = new URLSearchParams();
 *     if (state.search) params.append("q[search]", state.search);
 *     return params.toString();
 *   },
 * });
 * ```
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useLocation, useSearchParams } from "react-router";
import { registerFilterUrlKeys } from "@/shared/utils/persistedFilters";
import {
  resolveInitialState,
  applyUrlBudget,
  writePersisted,
} from "./urlSyncHelpers";
import type {
  FilterValue,
  FilterSetters,
  FiltersContextValue,
  FiltersLogicResult,
  CreateFiltersContextConfig,
  FiltersContextFactory,
} from "./types";

export const DEFAULT_DEBOUNCE_MS = 750;

/**
 * Capitalizes the first letter of a string
 */
const capitalize = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Internal hook for filter logic - shared between provider variants
 */
function useFiltersLogic<T extends Record<string, FilterValue>>(
  initialState: T,
  debounceMs: number,
  buildQueryString: (state: T) => string,
  // Seeds the initial UI/debounced state only (e.g. resolved from URL/storage).
  // Reset and setters keep targeting `initialState` (the true defaults).
  seedState: T = initialState,
): FiltersLogicResult<T> {
  // Current filter state (for UI)
  const [filters, setFilters] = useState<T>(seedState);

  // Debounced filter state (for queries)
  const [debouncedFilters, setDebouncedFilters] = useState<T>(seedState);

  // Debouncing state
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle debouncing
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setIsDebouncing(true);

    debounceRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
      setIsDebouncing(false);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [filters, debounceMs]);

  // Reset all filters
  const resetAllFilters = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setFilters(initialState);
    setDebouncedFilters(initialState);
    setIsDebouncing(false);
  }, [initialState]);

  // Apply all filters immediately (skip debounce)
  const applyAllFilters = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setDebouncedFilters(filters);
    setIsDebouncing(false);
  }, [filters]);

  // Generate setters dynamically
  const setters = useMemo(() => {
    const result = {} as FilterSetters<T>;

    for (const key of Object.keys(initialState) as Array<keyof T>) {
      const setterName = `set${capitalize(String(key))}` as keyof FilterSetters<T>;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (result as any)[setterName] = (value: T[typeof key]) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
      };
    }

    return result;
  }, [initialState]);

  // Generate query string from debounced state
  const queryString = useMemo(
    () => buildQueryString(debouncedFilters),
    [debouncedFilters, buildQueryString],
  );

  // Whether the current (non-debounced) state diverges from the defaults. Uses
  // the same JSON serialization as writePersisted, so at rest it mirrors exactly
  // whether a localStorage key is present.
  const hasActiveFilters = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(initialState),
    [filters, initialState],
  );

  return {
    filters,
    setFilters,
    debouncedFilters,
    setDebouncedFilters,
    setters,
    resetAllFilters,
    applyAllFilters,
    queryString,
    isDebouncing,
    hasActiveFilters,
  };
}

/**
 * Maps the internal logic object to the public context value, shared by every
 * provider variant so a new context field is added in one place.
 */
const buildContextValue = <T extends Record<string, FilterValue>>(
  logic: FiltersLogicResult<T>,
): FiltersContextValue<T> => ({
  filters: logic.filters,
  debouncedFilters: logic.debouncedFilters,
  setters: logic.setters,
  resetAllFilters: logic.resetAllFilters,
  applyAllFilters: logic.applyAllFilters,
  queryString: logic.queryString,
  isDebouncing: logic.isDebouncing,
  hasActiveFilters: logic.hasActiveFilters,
});

/**
 * Memoizes the context value so consumers of `useFilters` only re-render when a
 * filter field actually changes, not on every provider render. Shared by all
 * provider variants.
 */
const useFiltersContextValue = <T extends Record<string, FilterValue>>(
  logic: FiltersLogicResult<T>,
): FiltersContextValue<T> =>

  useMemo(() => buildContextValue(logic), [
    logic.filters,
    logic.debouncedFilters,
    logic.setters,
    logic.resetAllFilters,
    logic.applyAllFilters,
    logic.queryString,
    logic.isDebouncing,
    logic.hasActiveFilters,
  ]);

/**
 * Creates a complete filters context system
 */
export function createFiltersContext<T extends Record<string, FilterValue>>(
  config: CreateFiltersContextConfig<T>,
): FiltersContextFactory<T> {
  const {
    name,
    initialState,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    buildQueryString,
    resetOnPathChange,
  } = config;

  // Serialized once here so writePersisted compares against it on every debounce
  // flush without re-stringifying the constant defaults each time.
  const initialStateSerialized = JSON.stringify(initialState);

  // Create the context
  const Context = createContext<FiltersContextValue<T> | undefined>(undefined);
  Context.displayName = `${name}FiltersContext`;

  // Provider with location tracking (for route-based reset)
  const ProviderWithLocation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const logic = useFiltersLogic(initialState, debounceMs, buildQueryString);
    const { setFilters, setDebouncedFilters } = logic;

    // Reset on path change. Depend on the stable setState dispatchers, not the
    // whole `logic` object (recreated every render), so this fires only on a
    // pathname change.
    useEffect(() => {
      if (resetOnPathChange && !location.pathname.startsWith(resetOnPathChange)) {
        setFilters(initialState);
        setDebouncedFilters(initialState);
      }
    }, [location.pathname, setFilters, setDebouncedFilters]);

    const contextValue = useFiltersContextValue(logic);
    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
  };

  // Provider without location tracking (simpler, no router dependency)
  const ProviderWithoutLocation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const logic = useFiltersLogic(initialState, debounceMs, buildQueryString);

    const contextValue = useFiltersContextValue(logic);
    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
  };

  // Provider with opt-in URL + localStorage synchronization
  const ProviderWithUrlSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // Parse-once at mount (D2): searchParams is read a single time, never an
    // effect dep, so back/forward navigation does not re-seed the state.

    const seed = useMemo(() => resolveInitialState(config, searchParams), []);
    const logic = useFiltersLogic(initialState, debounceMs, buildQueryString, seed);
    const { setFilters, setDebouncedFilters } = logic;

    // state -> URL (scoped to urlKeys) + localStorage. No mount guard (B1), so the
    // URL is rewritten immediately (even after a localStorage restore). There is
    // deliberately no [searchParams] effect (D2): sync is unidirectional.
    // Note: resetAllFilters resets React state instantly, but this effect (and
    // thus the URL/localStorage cleanup) only runs on the next debounced cycle.
    useEffect(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const sync = config.urlSync;
        if (!sync) return next;
        sync.urlKeys.forEach((key) => next.delete(key));
        for (const [key, value] of applyUrlBudget(sync.serialize(logic.debouncedFilters))) {
          next.append(key, value);
        }
        return next;
      }, { replace: true });
      writePersisted(config.urlSync?.storageKey, logic.debouncedFilters, initialStateSerialized);
    }, [logic.debouncedFilters, setSearchParams]);

    // Lets a voluntary logout drop this page's filter params from the return URL.
    useEffect(() => {
      if (config.urlSync) registerFilterUrlKeys(location.pathname, config.urlSync.urlKeys);
    }, [location.pathname]);

    // Optional route-based reset, composed here so urlSync can coexist with it.
    // Depends on the stable setState dispatchers, not the whole `logic` object.
    useEffect(() => {
      if (resetOnPathChange && !location.pathname.startsWith(resetOnPathChange)) {
        setFilters(initialState);
        setDebouncedFilters(initialState);
      }
    }, [location.pathname, setFilters, setDebouncedFilters]);

    const contextValue = useFiltersContextValue(logic);
    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
  };

  // Choose provider based on config
  const selectProvider = (): React.FC<{ children: React.ReactNode }> => {
    if (config.urlSync) return ProviderWithUrlSync;
    if (resetOnPathChange) return ProviderWithLocation;
    return ProviderWithoutLocation;
  };
  const Provider = selectProvider();
  Provider.displayName = `${name}FiltersProvider`;

  // Create the hook
  const useFilters = (): FiltersContextValue<T> => {
    const context = useContext(Context);
    if (context === undefined) {
      throw new Error(
        `use${name}Filters must be used within a ${name}FiltersProvider`,
      );
    }
    return context;
  };

  return {
    Context,
    Provider,
    useFilters,
  };
}
